import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trans } from '@lingui/macro'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { useSnackbar } from 'notistack'
import { Box } from '@mui/material'

import { UpdatePatientDto } from '@/core/models/patient.model'
import { Gender } from '@/core/types/shared'
import { PatientConsultations } from '@/features/patient/patient-consultations'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'
import { getPatient, selectPatientById, selectPatientUpdateRequestTracker, updatePatient } from '@/redux/reducers/patient.reducer'
import { transformObject } from '@/utils/helpers'
import { DMPConnectVitaleCardDialog, PlatformVitaleCardDialog, useSelectVitaleCardInfo, useVitaleCardDisplayLogic } from '@/widgets/vitale-card'

import PatientDocuments from './PatientDocuments'
import { ConsultationInfo } from './ConsultationInfo'
import PatientInfo, { PatientInfoProps } from '../components/PatientInfo'

type PatientInfoContainerProps = {
  consultationId?: string
  // The carte Vitale reader is offered in every consultation's patient info tab (fr only, see PatientInfo).
  showVitaleCard?: boolean
  // Emergency room: the card identifies the anonymous « Urgence » placeholder patient, so only offer it for
  // patients created there (agreement 'Emergency…'), never to overwrite an identified patient from that room.
  vitaleCardForEmergencyPatientsOnly?: boolean
  showHistory?: boolean
}

export function PatientInfoContainer(props: PatientInfoContainerProps) {
  const { consultationId, showVitaleCard: showVitaleCardProp = true, vitaleCardForEmergencyPatientsOnly = false, showHistory } = props
  const appDispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { showDMPConnectVitaleCard, showPlatformVitaleCard } = useVitaleCardDisplayLogic()
  const { vitaleCardInfo, onDMPConnectVitaleCardSelect, onPlatformVitaleCardSelect } = useSelectVitaleCardInfo()

  const [openCarteVitale, setOpenCarteVitale] = useState<boolean>(false)
  const [carteVitaleSuccess, setCarteVitaleSuccess] = useState<boolean>(false)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const patient = useAppSelector((state) => (consultation ? selectPatientById(state, consultation.patient._id) : undefined))
  const patientId = useMemo(() => (consultation ? consultation.patient._id : undefined), [consultation])
  const showVitaleCard = showVitaleCardProp && (!vitaleCardForEmergencyPatientsOnly || !!patient?.agreement?.toLowerCase().startsWith('emergency'))

  const patientInfos = useMemo(() => {
    return _.merge(
      {
        notes: patient?.notes || '',
        pluginId: patient?.pluginId ?? patient?.netsoinsId ?? '',
        background: patient?.background || '',
        treatment: patient?.treatment || '',
        privateDoctor: patient?.attendingPhysician
          ? {
              firstname: patient.attendingPhysician?.firstname || '',
              lastname: patient.attendingPhysician?.lastname || '',
              email: patient.attendingPhysician?.email || '',
              phone: patient.attendingPhysician?.phone || '',
              city: patient.attendingPhysician?.city || '',
              cp: _.toString(patient.attendingPhysician?.cp || ''),
            }
          : null,
        parents: _.map(patient?.parents, (parent) => ({
          firstname: parent.firstname,
          lastname: parent.lastname,
          email: parent.email || '',
          phone: parent.phone || '',
          city: parent.city || '',
          cp: (parent.cp || '') + '',
        })),
        insured: patient?.insured || '',
        insuredRelationship: patient?.insuredRelationship || '',
        insuranceProvider: patient?.insuranceProvider || '',
        primaryInsurance: patient?.primaryInsurance || '',
        secondaryInsurance: patient?.secondaryInsurance || '',
        primaryPolicy: patient?.primaryPolicy || '',
        secondaryPolicy: patient?.secondaryPolicy || '',
        primaryPharmacy: patient?.primaryPharmacy || '',
      },
      Object.keys(vitaleCardInfo).length > 0
        ? {
            name: vitaleCardInfo.name || '',
            surname: vitaleCardInfo.surname || '',
            email: vitaleCardInfo.email || '',
            gender: vitaleCardInfo.gender || '',
            phone: patient?.contactInfo?.phone || '',
            birthday: !vitaleCardInfo.birthday ? null : new Date(vitaleCardInfo.birthday),
            secu: vitaleCardInfo.secu || '',
            caisse: vitaleCardInfo.caisse || '',
            codeRegime: vitaleCardInfo.codeRegime || '',
            codeGestion: vitaleCardInfo.codeGestion || '',
          }
        : {
            name: patient?.userInfo.name || '',
            surname: patient?.userInfo.surname || '',
            email: patient?.email || '',
            gender: patient?.userInfo.gender || '',
            phone:
              !!patient && patient.contactInfo && typeof patient.contactInfo.phone === 'string' && isPossiblePhoneNumber(patient.contactInfo.phone)
                ? patient.contactInfo.phone
                : '',
            birthday: !patient?.userInfo.birthday ? null : new Date(patient.userInfo.birthday),
            secu: patient?.secu ? patient.secu : '',
            caisse: patient?.caisse || '',
            codeRegime: patient?.codeRegime || '',
            codeGestion: patient?.codeGestion || '',
          }
    )
  }, [consultation, patient, vitaleCardInfo])

  const { dispatch: requestHandlerDispatch, requestTracker: patientUpdateRequestTracker } = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.patient.update.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.patient.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectPatientUpdateRequestTracker,
  })

  useEffect(() => {
    if (patientId) {
      appDispatch(getPatient(patientId))
    }
  }, [appDispatch, patientId])

  const transformData = (values: PatientInfoProps['infos']): UpdatePatientDto => {
    return {
      contactInfo: transformObject({
        phone: values.phone,
      }),
      userInfo: transformObject({
        birthday: values.birthday?.toISOString(),
        gender: values.gender as Gender,
        name: values.name,
        surname: values.surname,
      }),
      ...transformObject({
        email: values.email,
        secu: values.secu,
        caisse: values.caisse,
        codeRegime: values.codeRegime,
        codeGestion: values.codeGestion,
        notes: values.notes,
        pluginId: values.pluginId,
        netsoinsId: values.pluginId,
        background: values.background,
        treatment: values.treatment,
        insured: values.insured,
        insuredRelationship: values.insuredRelationship,
        insuranceProvider: values.insuranceProvider,
        primaryInsurance: values.primaryInsurance,
        secondaryInsurance: values.secondaryInsurance,
        primaryPolicy: values.primaryPolicy,
        secondaryPolicy: values.secondaryPolicy,
        primaryPharmacy: values.primaryPharmacy,
      }),
      parents: values.parents?.map((parent) => ({
        ...transformObject(parent),
        cp: parent.cp,
      })),
      attendingPhysician:
        values.privateDoctor && values.privateDoctor.firstname && values.privateDoctor.lastname
          ? {
              ...transformObject(values.privateDoctor),
              cp: values.privateDoctor.cp,
            }
          : null,
    }
  }

  const handleModifyPatient = (values: PatientInfoProps['infos']) => {
    const updatePatientDto = transformData(values)
    if (patient?._id) requestHandlerDispatch(updatePatient(patient._id, updatePatientDto))
  }

  return (
    <>
      <ConsultationInfo consultationId={consultationId} />
      <PatientInfo
        infos={patientInfos}
        isModifying={patientUpdateRequestTracker.loading}
        onModifyPatient={handleModifyPatient}
        onGetCarteVitale={() => setOpenCarteVitale(true)}
        readOnly={false}
        carteVitaleSuccess={carteVitaleSuccess}
        showVitaleCard={showVitaleCard}
      />
      {patientId && (
        <Box px={3}>
          <PatientDocuments patientId={patientId} />
        </Box>
      )}
      {patientId && showHistory && (
        <Box px={3}>
          <PatientConsultations patientId={patientId} />
        </Box>
      )}
      {showVitaleCard && showDMPConnectVitaleCard && patientId && (
        <DMPConnectVitaleCardDialog
          open={openCarteVitale}
          onClose={() => setOpenCarteVitale(false)}
          patientId={patientId}
          title={t`Add by vitale card`}
          onSelectPatient={(patient) => {
            onDMPConnectVitaleCardSelect(patient)
            // The card values become the form's initial values (enableReinitialize), so the form is not dirty:
            // without this the « Modifier » button would stay disabled and the card's secu could not be saved.
            setCarteVitaleSuccess(true)
            setOpenCarteVitale(false)
          }}
        />
      )}
      {showVitaleCard && showPlatformVitaleCard && (
        <PlatformVitaleCardDialog
          onClose={() => setOpenCarteVitale(false)}
          onSelect={(e) => {
            onPlatformVitaleCardSelect(e)
            setCarteVitaleSuccess(true)
          }}
          open={openCarteVitale}
        />
      )}
    </>
  )
}

export default PatientInfoContainer
